import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEntityDoc, useEntityList, friendlyError } from '../../data/entities';
import { savePolicy } from '../../data/policy';
import PolicyHistory from './PolicyHistory';
import './AppsModule.css';

// Org Costs card (Billing module, admins). Reads the hourly rollup
// orgs/{org}/costs/{YYYY-MM} (api/costRollupWorker.js): MTD / burn /
// projection against the org's monthly limit, by app, by user (org members
// and each app's own end-users), plus the fired-alert history. Limits are
// alerts only — the copy says so because nobody should expect a hard stop.

const fmt = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const BASIS_LABEL = { actual: 'actual', estimated: 'estimated' };

function Basis({ basis }) {
  if (!basis) return null;
  return <span className={`ap-badge ${basis === 'actual' ? 'is-ok' : ''}`} style={{ marginLeft: 6, fontSize: 10 }}>{BASIS_LABEL[basis] || basis}</span>;
}

function LimitBar({ mtdUsd, projectedUsd, limitUsd }) {
  if (!(limitUsd > 0)) return null;
  const pct = Math.min(100, (mtdUsd / limitUsd) * 100);
  const ppct = Math.min(100, (projectedUsd / limitUsd) * 100);
  const color = pct >= 100 ? 'var(--danger)' : pct >= 80 || ppct >= 100 ? 'var(--warn)' : 'var(--ok)';
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'var(--line)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, width: `${ppct}%`, background: color, opacity: 0.3 }} />
        <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: color }} />
      </div>
      <div className="cs-help" style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
        <span>{Math.round((mtdUsd / limitUsd) * 100)}% of {fmt(limitUsd)} used · projected {Math.round((projectedUsd / limitUsd) * 100)}%</span>
        <span>alerts at 50 / 80 / 100% — nothing is paused</span>
      </div>
    </div>
  );
}

function LimitEditor({ org, canEdit, apps }) {
  const limits = org.costLimits || {};
  const [monthly, setMonthly] = useState(limits.monthlyUsd ?? '');
  const [appLimits, setAppLimits] = useState(limits.apps || {});
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  if (!canEdit) return null;

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const apps = {};
      for (const [id, v] of Object.entries(appLimits)) if (Number(v) > 0) apps[id] = Number(v);
      // Through the policy API: a numbered, restorable revision + audit row.
      await savePolicy(org.id, 'costLimits', { monthlyUsd: Number(monthly) > 0 ? Number(monthly) : null, apps });
      setOpen(false);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return <button type="button" className="cs-btn" onClick={() => setOpen(true)}>{limits.monthlyUsd ? 'Edit limits' : 'Set a monthly limit'}</button>;
  }
  return (
    <div className="cs-card" style={{ padding: 14, marginTop: 10, display: 'grid', gap: 10, maxWidth: 480 }}>
      <label style={{ display: 'grid', gap: 4 }}>
        <span className="cs-help" style={{ margin: 0 }}>Organization monthly limit (USD)</span>
        <input className="cs-input" type="number" min="0" step="1" value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="e.g. 500" />
      </label>
      {apps.map((a) => (
        <label key={a.id} style={{ display: 'grid', gap: 4 }}>
          <span className="cs-help" style={{ margin: 0 }}>{a.name || a.slug} limit (USD, optional)</span>
          <input className="cs-input" type="number" min="0" step="1" value={appLimits[a.id] ?? ''} onChange={(e) => setAppLimits({ ...appLimits, [a.id]: e.target.value })} />
        </label>
      ))}
      <p className="cs-help" style={{ margin: 0 }}>
        Crossing 50%, 80% and 100% of a limit — or projecting to exceed it — emails org admins once per month. Nothing is blocked or suspended.
      </p>
      {err && <div className="cs-form-error">{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="cs-btn cs-btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save limits'}</button>
        <button type="button" className="cs-btn" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function CostsCard({ org, myRole }) {
  const period = new Date().toISOString().slice(0, 7);
  const canSee = myRole === 'admin' || myRole === 'owner';
  const { doc: rollup } = useEntityDoc('orgCost', { org: org.id }, canSee ? period : null);
  const { doc: alerts } = useEntityDoc('costAlert', { org: org.id }, canSee ? period : null);
  const { docs: members } = useEntityList('member', { org: org.id }, { enabled: canSee });
  const { docs: apps } = useEntityList('appInstance', { org: org.id }, { enabled: canSee });
  const { docs: statements } = useEntityList('invoiceStatement', { org: org.id }, { enabled: canSee });
  const nameOf = useMemo(() => {
    const m = {};
    for (const x of members || []) m[x.id] = x.email || x.id;
    m.system = 'System (scheduled / automated)';
    return m;
  }, [members]);
  const [showUsers, setShowUsers] = useState(false);

  if (!canSee) return null;
  const limitUsd = org.costLimits?.monthlyUsd || 0;
  const state = org.costAlertState;
  const allApps = Object.entries(rollup?.byApp || {}).sort((a, b) => b[1].mtdUsd - a[1].mtdUsd);
  const byApp = allApps.filter(([, a]) => a.mtdUsd > 0);
  const zeroApps = allApps.length - byApp.length;
  const byUser = Object.entries(rollup?.byUser || {}).sort((a, b) => b[1] - a[1]).slice(0, 15);
  const appUsers = Object.entries(rollup?.appUsers || {});
  const platform = rollup?.platform;
  const model = rollup?.model;
  const byPipeline = Object.entries(rollup?.byPipeline || {}).filter(([, p]) => p.usd > 0).sort((a, b) => b[1].usd - a[1].usd).slice(0, 12);
  const history = [...(alerts?.history || [])].reverse().slice(0, 8);
  const recentStatements = [...(statements || [])].sort((a, b) => (a.period < b.period ? 1 : -1)).slice(0, 6);

  return (
    <section style={{ marginTop: 28 }}>
      <h2 style={{ margin: 0 }}>Costs · {period}</h2>
      {state && state.level !== 'ok' && (
        <div className={`ap-banner ${state.level === 'over' ? 'is-err' : 'is-warn'}`} style={{ marginTop: 10 }}>{state.message}</div>
      )}
      {rollup === null && <div className="cs-empty" style={{ marginTop: 10 }}>No cost data yet — the first rollup runs within the hour after any metered activity or hosted-app spend.</div>}
      {rollup && (
        <>
          <div className="ap-health-stats" style={{ marginTop: 12 }}>
            <div className="ap-health-stat"><strong>{fmt(rollup.mtdUsd)}</strong><span className="cs-help">month to date</span></div>
            <div className="ap-health-stat"><strong>{fmt(rollup.burnPerDayUsd)}</strong><span className="cs-help">burn / day</span></div>
            <div className="ap-health-stat"><strong>{fmt(rollup.projectedUsd)}</strong><span className="cs-help">projected month end</span></div>
            <div className="ap-health-stat"><strong>{limitUsd ? fmt(limitUsd) : 'none'}</strong><span className="cs-help">monthly limit</span></div>
          </div>
          <LimitBar mtdUsd={rollup.mtdUsd} projectedUsd={rollup.projectedUsd} limitUsd={limitUsd} />
        </>
      )}
      <div style={{ marginTop: 10 }}><LimitEditor org={org} canEdit={canSee} apps={apps || []} /></div>
      <PolicyHistory org={org} isAdmin={canSee} area="costLimits" compact />

      {rollup?.credits && !rollup.credits.unlimited && (
        <div className={`cs-card ${rollup.credits.exhausted ? 'ap-banner is-err' : ''}`} style={{ padding: 14, marginTop: 14, display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <strong>Model credits · {fmt(rollup.credits.remainingUsd)} left this month</strong>
            <Link to={`/orgs/${org.id}/administration?tab=models`} className="cs-btn cs-btn-sm cs-btn-outline">{rollup.credits.exhausted ? 'Add credits' : 'Models & credits'}</Link>
          </div>
          <div className="cs-help" style={{ margin: 0 }}>
            {fmt(rollup.credits.includedUsd)} included with the plan + {fmt(rollup.credits.balanceUsd)} prepaid · {fmt(rollup.credits.spentUsd)} of hosted model usage so far.
            {rollup.credits.exhausted
              ? ' Hosted model calls are paused (402) until credits are added — the app itself keeps running; your own keys are never paused.'
              : ' Unlike the limit above, this one does stop hosted model calls at $0 — top up before it runs out.'}
          </div>
        </div>
      )}

      {rollup && (
        <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginTop: 18 }}>
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>By app</h3>
            {!byApp.length && <div className="cs-help">No hosted-app spend this month.</div>}
            <div className="ap-cost-services" style={{ marginTop: 0 }}>
              {byApp.map(([appId, a]) => (
                <div key={appId} className="ap-cost-service" style={{ display: 'grid', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span className="ap-cost-svc-name" style={{ color: 'var(--text)' }}>{a.name}</span>
                    <span className="ap-cost-svc-usd">{fmt(a.mtdUsd)}</span>
                  </div>
                  <div className="cs-help" style={{ margin: 0, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {a.infra && <span>infra {fmt(a.infra.usd)}<Basis basis="actual" /></span>}
                    {a.model && a.model.usd > 0 && <span>models {fmt(a.model.usd)}<Basis basis="estimated" /></span>}
                    {a.dev && a.dev.usd > 0 && <span>dev console {fmt(a.dev.usd)}<Basis basis="actual" /></span>}
                    {a.platform?.usd > 0 && <span>platform {fmt(a.platform.usd)}<Basis basis="estimated" /></span>}
                    {org.costLimits?.apps?.[appId] > 0 && <span>limit {fmt(org.costLimits.apps[appId])}</span>}
                  </div>
                </div>
              ))}
              {zeroApps > 0 && <div className="cs-help" style={{ margin: '4px 0 0' }}>{zeroApps} app{zeroApps > 1 ? 's' : ''} with no spend recorded yet (fleet host share lands with the next AWS pull).</div>}
              {platform && platform.usd > 0 && (
                <div className="ap-cost-service">
                  <span className="ap-cost-svc-name">Platform usage (pipelines, jobs, copilot…)<Basis basis="estimated" /></span>
                  <span className="ap-cost-svc-usd">{fmt(platform.usd)}</span>
                </div>
              )}
              {model && (model.usd > 0 || model.byoTokens > 0) && (
                <div className="ap-cost-service" style={{ display: 'grid', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span className="ap-cost-svc-name">Hosted model tokens (copilot, workflows, agents, voice)<Basis basis="estimated" /></span>
                    <span className="ap-cost-svc-usd">{fmt(model.usd)}</span>
                  </div>
                  <div className="cs-help" style={{ margin: 0 }}>
                    {Number(model.tokens || 0).toLocaleString()} tokens on nventr keys
                    {model.byoTokens > 0 && <> · {Number(model.byoTokens).toLocaleString()} tokens on your own keys (not billed)</>}
                  </div>
                </div>
              )}
            </div>
            {byPipeline.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>By workflow</h3>
                <div className="ap-cost-services" style={{ marginTop: 0 }}>
                  {byPipeline.map(([pid, p]) => (
                    <div key={pid} className="ap-cost-service" style={{ display: 'grid', gap: 2 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span className="ap-cost-svc-name" style={{ color: 'var(--text)' }}>{p.name || pid}</span>
                        <span className="ap-cost-svc-usd">{fmt(p.usd)}</span>
                      </div>
                      <div className="cs-help" style={{ margin: 0 }}>
                        {p.modelUsd > 0 && <>models {fmt(p.modelUsd)} · </>}runs &amp; blocks {fmt(p.platformUsd)}<Basis basis="estimated" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>By user</h3>
            {!byUser.length && !appUsers.length && <div className="cs-help">No attributable usage yet.</div>}
            <div className="ap-cost-services" style={{ marginTop: 0 }}>
              {byUser.map(([uid, usd]) => (
                <div key={uid} className="ap-cost-service">
                  <span className="ap-cost-svc-name">{nameOf[uid] || uid}</span>
                  <span className="ap-cost-svc-usd">{fmt(usd)}</span>
                </div>
              ))}
            </div>
            {appUsers.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <button type="button" className="cs-btn-link" onClick={() => setShowUsers(!showUsers)}>
                  {showUsers ? 'Hide' : 'Show'} end-users inside your apps
                </button>
                {showUsers && appUsers.map(([appId, users]) => (
                  <div key={appId} style={{ marginTop: 8 }}>
                    <div className="cs-help" style={{ margin: '0 0 4px' }}>{rollup.byApp?.[appId]?.name || appId} · model spend (estimated)</div>
                    <div className="ap-cost-services" style={{ marginTop: 0 }}>
                      {Object.entries(users).sort((a, b) => b[1].usd - a[1].usd).slice(0, 10).map(([uid, u]) => (
                        <div key={uid} className="ap-cost-service">
                          <span className="ap-cost-svc-name">{uid === 'other' ? 'Everyone else' : uid}</span>
                          <span className="ap-cost-svc-usd">{fmt(u.usd)} <span className="cs-help" style={{ margin: 0, display: 'inline' }}>· {u.tokens.toLocaleString()} tok</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {recentStatements.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Monthly statements</h3>
          <p className="cs-help" style={{ margin: '0 0 8px' }}>
            Closed on the 2nd of each month. Your subscription is charged separately; these are the metered lines (included quotas already deducted).
          </p>
          <div className="ap-cost-services" style={{ marginTop: 0 }}>
            {recentStatements.map((s) => {
              const billable = (s.lines || []).filter((l) => l.amountCents > 0);
              return (
                <div key={s.id} className="ap-cost-service" style={{ display: 'grid', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span className="ap-cost-svc-name" style={{ color: 'var(--text)' }}>
                      {s.period} · {s.kind === 'platform' ? 'Platform' : rollup?.byApp?.[s.appId]?.name || s.slug || 'App'}
                      <span className={`ap-badge ${s.status === 'draft' ? '' : 'is-ok'}`} style={{ marginLeft: 6, fontSize: 10 }}>{s.status}</span>
                    </span>
                    <span className="ap-cost-svc-usd">{fmt((s.subtotalCents || 0) / 100)}</span>
                  </div>
                  <div className="cs-help" style={{ margin: 0 }}>
                    {billable.length ? billable.map((l) => `${l.label}: ${l.qty} ${l.unit}`).join(' · ') : 'Within included quotas — nothing metered to charge.'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Alerts this month</h3>
          <div className="ap-cost-services" style={{ marginTop: 0 }}>
            {history.map((h) => (
              <div key={`${h.scope}-${h.threshold}-${h.at}`} className="ap-cost-service">
                <span className="ap-cost-svc-name">
                  {h.scope === 'org' ? 'Organization' : rollup?.byApp?.[h.scope.replace('app:', '')]?.name || h.scope} · {h.kind === 'projected' ? 'projected to exceed' : `${Math.round(h.threshold * 100)}%`}
                </span>
                <span className="cs-help" style={{ margin: 0 }}>{fmt(h.mtdUsd)} of {fmt(h.limitUsd)} · {new Date(h.at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
